import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import CustomerListCounters from "@/components/customers/list/CustomerListCounters";
import CustomerListToolbar, { type CustomerListToolbarState } from "@/components/customers/list/CustomerListToolbar";
import CustomerListV2Table from "@/components/customers/list/CustomerListV2Table";
import CustomerOwnersDialog from "@/components/customers/CustomerOwnersDialog";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useCustomerList } from "@/hooks/use-customer-list";
import {
  customerListCounters,
  filterCustomerRows,
  sortCustomerRows,
  type CustomerListCounterFilter,
  type CustomerListSort,
} from "@/lib/customer-list-calc";

const INITIAL: CustomerListToolbarState = { search: "", owner: "all", status: "all" };

/**
 * Kundeliste v2 (VIB-2891, bak feature_crm_kundekort): tellere som også er
 * filtre, verktøylinje, og radene gruppert etter hva som haster («Haster
 * mest») eller sortert flatt på en kolonne. Tellerne regnes på alle kunder
 * før filtrene, så de alltid svarer på «hva ligger og venter».
 */
export default function CustomerListV2() {
  const { rows, isLoading, error } = useCustomerList();
  const { activeUser } = useImpersonation();
  const [state, setState] = useState<CustomerListToolbarState>(INITIAL);
  const [counter, setCounter] = useState<CustomerListCounterFilter | null>(null);
  const [sort, setSort] = useState<CustomerListSort | null>(null);
  const [claimFor, setClaimFor] = useState<string | null>(null);

  const counters = useMemo(() => customerListCounters(rows), [rows]);
  const visible = useMemo(
    () => sortCustomerRows(filterCustomerRows(rows, { ...state, myUserId: activeUser?.id ?? null, counter }), sort),
    [rows, state, activeUser?.id, counter, sort],
  );
  const unownedCount = useMemo(() => rows.filter((r) => r.owners.length === 0).length, [rows]);
  const isFiltered = !!state.search || state.owner !== "all" || state.status !== "all" || counter !== null;

  if (error) {
    return <p className="text-destructive">Feil ved lasting av kunder: {error.message}</p>;
  }

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Laster tellere">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <CustomerListCounters counters={counters} active={counter} onToggle={(f) => setCounter((c) => (c === f ? null : f))} />
      )}

      <Card>
        <CardContent className="space-y-4 pt-6">
          <CustomerListToolbar
            state={state}
            onChange={(patch) => setState((s) => ({ ...s, ...patch }))}
            unownedCount={unownedCount}
            groupedByUrgency={sort === null}
            onGroupByUrgency={() => setSort(null)}
          />
          {isLoading ? (
            <Skeleton className="h-64" />
          ) : (
            <>
              <CustomerListV2Table
                rows={visible}
                sort={sort}
                onSort={setSort}
                emptyMessage={isFiltered ? "Ingen kunder matcher filteret." : "Ingen kunder."}
                onClaimOwnership={setClaimFor}
              />
              <p className="text-xs text-muted-foreground">Viser {visible.length} av {rows.length}. Alle beløp er GEEK.</p>
            </>
          )}
        </CardContent>
      </Card>

      {claimFor && (
        <CustomerOwnersDialog open onOpenChange={(open) => !open && setClaimFor(null)} customerId={claimFor} />
      )}
    </div>
  );
}
